import { COURSE_REFUND_WINDOW_DAYS } from './types';

/*
 * Os códigos de erro da Members API (members-api/src/members_api/domain.py) em que
 * a UI ancora — nunca na mensagem, que é texto para o leitor e pode mudar.
 * `members-api/tests/test_route_parity.py` lê estas constantes como texto e exige
 * que cada valor exista no servidor (os só da Edge Function ficam em
 * `EDGE_ONLY_CODES` lá); os códigos que só o cliente fabrica
 * (`network_error`, `http_<status>`) ficam fora daqui de propósito.
 */
export const UNAUTHENTICATED = 'unauthenticated';
export const ENTITLEMENT_REQUIRED = 'entitlement_required';
export const NOT_FOUND = 'not_found';
export const COURSE_NOT_FOUND = 'course_not_found';
export const LESSON_NOT_FOUND = 'lesson_not_found';
export const PURCHASE_NOT_FOUND = 'purchase_not_found';
/** Já existe entitlement para o curso (inscrição ou compra anterior). */
export const ALREADY_ENTITLED = 'already_entitled';
/** Outra requisição está criando a compra deste curso agora (corrida, ou reenvio antes de a cobrança existir). */
export const PURCHASE_IN_PROGRESS = 'purchase_in_progress';
/** Cancelar uma compra pendente ou expirada: só a confirmada tem estorno. */
export const PURCHASE_NOT_CONFIRMED = 'purchase_not_confirmed';
export const REFUND_WINDOW_EXPIRED = 'refund_window_expired';
export const COURSE_IS_FREE = 'course_is_free';
export const COURSE_NOT_FREE = 'course_not_free';
export const INVALID_PURCHASE = 'invalid_purchase';
export const INVALID_METHOD = 'invalid_method';
export const INVALID_PAYER = 'invalid_payer';
export const INVALID_CARD = 'invalid_card';
export const EMAIL_REQUIRED = 'email_required';
/** O gateway recusou a cobrança; a `message` do servidor traz o motivo dele. */
export const PAYMENT_REFUSED = 'payment_refused';
export const PAYMENT_PROVIDER_UNAVAILABLE = 'payment_provider_unavailable';
export const REFUND_FAILED = 'refund_failed';
export const INVALID_JSON = 'invalid_json';
export const PAYLOAD_TOO_LARGE = 'payload_too_large';
export const INTERNAL_ERROR = 'internal_error';
// Só a Edge Function emite estes (a área de membros não existe na Lambda de rollback).
export const INVALID_PROFILE = 'invalid_profile';
export const INVALID_NOTIFICATION = 'invalid_notification';
export const INVALID_SITE_SETTINGS = 'invalid_site_settings';
export const INVALID_SITE_LINKS = 'invalid_site_links';
export const INVALID_TEAM = 'invalid_team';
export const NOTIFICATION_NOT_FOUND = 'notification_not_found';

/**
 * A frase pt-BR de cada código — a mesma que o servidor envia no envelope. O
 * mock lança exatamente estas; o client HTTP as usa quando o envelope chega sem
 * `message`.
 */
export const BLOG_API_ERROR_MESSAGES = {
  [UNAUTHENTICATED]: 'Entre na sua conta para continuar.',
  [ENTITLEMENT_REQUIRED]: 'Inscreva-se ou compre o curso para abrir esta lição.',
  [NOT_FOUND]: 'Recurso não encontrado.',
  [COURSE_NOT_FOUND]: 'Curso não encontrado.',
  [LESSON_NOT_FOUND]: 'Lição não encontrada.',
  [PURCHASE_NOT_FOUND]: 'Compra não encontrada.',
  [ALREADY_ENTITLED]: 'Você já tem acesso a este curso.',
  [PURCHASE_IN_PROGRESS]: 'Já existe uma compra deste curso em andamento. Aguarde um instante.',
  [PURCHASE_NOT_CONFIRMED]: 'Só é possível cancelar uma compra já confirmada.',
  [REFUND_WINDOW_EXPIRED]: `O estorno automático vale até ${COURSE_REFUND_WINDOW_DAYS} dias após a confirmação do pagamento.`,
  [COURSE_IS_FREE]: 'Este curso é gratuito — basta se inscrever.',
  [COURSE_NOT_FREE]: 'Este curso é pago — conclua a compra para acessar.',
  [INVALID_PURCHASE]: 'Envie o meio de pagamento e os dados do pagador.',
  [INVALID_METHOD]: 'Escolha Pix ou cartão.',
  [INVALID_PAYER]: 'Confira o nome e o CPF/CNPJ do pagador.',
  [INVALID_CARD]: 'Confira os dados do cartão.',
  [EMAIL_REQUIRED]: 'Sua conta precisa de um e-mail para comprar.',
  [PAYMENT_REFUSED]: 'O meio de pagamento recusou a cobrança. Confira os dados ou tente outro meio.',
  [PAYMENT_PROVIDER_UNAVAILABLE]:
    'Não conseguimos falar com o meio de pagamento. Tente de novo em instantes.',
  [REFUND_FAILED]: 'Não foi possível concluir o estorno agora. Tente novamente em alguns minutos.',
  [INVALID_JSON]: 'O corpo da requisição não é um JSON válido.',
  [PAYLOAD_TOO_LARGE]: 'O corpo da requisição passa do limite de 64 KB.',
  [INTERNAL_ERROR]: 'Erro inesperado. Tente de novo em instantes.',
  [INVALID_PROFILE]: 'Confira o WhatsApp e as preferências.',
  [INVALID_NOTIFICATION]: 'Confira o título, o texto e o link da notificação.',
  [INVALID_SITE_SETTINGS]: 'Confira as configurações do site.',
  [INVALID_SITE_LINKS]: 'Confira os links: rótulo, destino e ícone.',
  [INVALID_TEAM]: 'Confira os dados da equipe.',
  [NOTIFICATION_NOT_FOUND]: 'Notificação não encontrada.',
} as const;

export type BlogApiErrorCode = keyof typeof BLOG_API_ERROR_MESSAGES;

const SERVER_FAILED_MESSAGE =
  'O servidor não respondeu como esperado. Tente novamente em instantes.';
const UNEXPECTED_ERROR_MESSAGE = 'Não foi possível concluir a operação. Tente novamente.';

/**
 * A frase para o leitor quando o servidor não mandou uma: a do código, ou a do
 * status quando o código é desconhecido (resposta sem envelope). Nunca o texto
 * cru da resposta — um "Bad Gateway" ou um HTML de proxy não é mensagem.
 */
export function messageForError(code: string, status: number): string {
  const known = (BLOG_API_ERROR_MESSAGES as Record<string, string>)[code];
  if (known) return known;
  if (status === 401) return BLOG_API_ERROR_MESSAGES[UNAUTHENTICATED];
  if (status === 404) return BLOG_API_ERROR_MESSAGES[NOT_FOUND];
  if (status >= 500) return SERVER_FAILED_MESSAGE;
  return UNEXPECTED_ERROR_MESSAGE;
}

/**
 * Erro de transporte normalizado da Members API. O client HTTP e o mock lançam
 * o MESMO tipo, para a UI tratar falhas de forma única (toasts, retry, 401 →
 * login). Mesma forma do ApiError da plataforma, em escopo próprio: este pacote
 * é o contrato do WS13 e não importa o client da plataforma.
 */
export class BlogApiError extends Error {
  readonly status: number;
  readonly code: string;

  /** Sem `message`, vale a frase pt-BR do código ({@link messageForError}). */
  constructor(status: number, body: { code: string; message?: string }) {
    super(body.message || messageForError(body.code, status));
    this.name = 'BlogApiError';
    this.status = status;
    this.code = body.code;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isServer(): boolean {
    return this.status >= 500;
  }
}

export function isBlogApiError(value: unknown): value is BlogApiError {
  return value instanceof BlogApiError;
}
